<template>
	<div class="campaigns-container">

		<!-- SEZIONE 1: Hero -->
		<section class="hero-section">
			<div class="hero-content">
				<h1 class="hero-title">
					{{ getString('campaigns_hero_title') }}
					<picture class="meta-logo-inline">
						<source :srcset="uiStore.darkMode ? 'https://img.qromo.io/img/section-console-campaigns/Meta-logo-white.webp' : 'https://img.qromo.io/img/section-console-campaigns/Meta-logo-dark.webp'" type="image/webp">
						<img :src="uiStore.darkMode ? 'https://img.qromo.io/img/section-console-campaigns/Meta-logo-white.png' : 'https://img.qromo.io/img/section-console-campaigns/Meta-logo-dark.png'" alt="Meta">
					</picture>
				</h1>
				<p class="hero-desc">{{ getString('campaigns_hero_desc_1') }}</p>
				<p class="hero-desc">{{ getString('campaigns_hero_desc_2') }}</p>
				<div class="social-logos">
					<img src="https://img.qromo.io/img/section-console-campaigns/Facebook-logo.svg" alt="Facebook">
					<img src="https://img.qromo.io/img/section-console-campaigns/Instagram-logo.svg" alt="Instagram">
				</div>
				<div class="hero-cta">
					<AppButton
						v-if="!isInterested"
						:text="getString('campaigns_im_interested')"
						:loading="isLoading"
						:disabled="isLoading"
						@click="handleInterestClick"
						class="cta-btn"
					/>
					<div v-else class="already-registered">
						<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M7.5 10L9.16667 11.6667L12.5 8.33333M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
						</svg>
						<span>{{ getString('campaigns_already_registered') }}</span>
					</div>
				</div>
			</div>
			<div class="hero-animation">
				<div id="campaigns-lottie-animation" class="lottie-container"></div>
			</div>
		</section>

		<!-- SEZIONE 2: Cosa sono le inserzioni Meta -->
		<section class="meta-section">
			<div class="meta-visual">
				<picture>
					<source srcset="https://img.qromo.io/img/section-console-campaigns/img-banner-campagne-opt.webp" type="image/webp">
					<img src="https://img.qromo.io/img/section-console-campaigns/img-banner-campagne-opt.png" alt="Meta campaigns">
				</picture>
			</div>
			<div class="meta-content">
				<h2 class="meta-title">{{ getString('campaigns_meta_title') }}</h2>
				<p class="meta-desc">{{ getString('campaigns_meta_desc_1') }}</p>
				<p class="meta-desc">{{ getString('campaigns_meta_desc_2') }}</p>
				<p class="meta-desc">{{ getString('campaigns_meta_desc_3') }}</p>
				<div class="meta-cta">
					<AppButton
						v-if="!isInterested"
						:text="getString('campaigns_im_interested')"
						:loading="isLoading"
						:disabled="isLoading"
						@click="handleInterestClick"
						class="cta-btn"
					/>
					<div v-else class="already-registered">
						<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M7.5 10L9.16667 11.6667L12.5 8.33333M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
						</svg>
						<span>{{ getString('campaigns_already_registered') }}</span>
					</div>
				</div>
			</div>
		</section>

		<!-- SEZIONE 3: Card risultati -->
		<section class="results-section">
			<h2 class="results-title">{{ getString('campaigns_results_title') }}</h2>
			<div class="results-grid">
				<div class="result-card" v-for="card in resultCards" :key="card.id">
					<img
						class="card-icon"
						:src="'https://img.qromo.io/img/section-console-campaigns/icon-' + card.id + (uiStore.darkMode ? '-dark' : '') + '.svg'"
						:alt="getString(card.titleKey)"
					>
					<h3 class="card-title">{{ getString(card.titleKey) }}</h3>
					<p class="card-desc">{{ getString(card.descKey) }}</p>
				</div>
			</div>
			<div class="results-cta">
				<AppButton
					v-if="!isInterested"
					:text="getString('campaigns_im_interested')"
					:loading="isLoading"
					:disabled="isLoading"
					@click="handleInterestClick"
					class="cta-btn"
				/>
				<div v-else class="already-registered">
					<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M7.5 10L9.16667 11.6667L12.5 8.33333M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
					</svg>
					<span>{{ getString('campaigns_already_registered') }}</span>
				</div>
			</div>
		</section>

	</div>
</template>

<script>
import {useLanguageStore, useNotificationStore, useUiStore} from "@/shared/stores";
import {useUserStore} from "@/console-new/stores/user.store";
import AppButton from "@/shared/components/AppButton.vue";
import API from "@/shared/helpers/API";

export default {
	name: "CampaignsContainer",
	components: {AppButton},
	setup() {
		const languageStore = useLanguageStore();
		const notificationStore = useNotificationStore();
		const userStore = useUserStore();
		const uiStore = useUiStore();
		return {
			languageStore,
			notificationStore,
			userStore,
			uiStore,
			getString: languageStore.getString,
			showNotification: notificationStore.showNotification
		};
	},
	data() {
		return {
			isInterested: false,
			isLoading: false,
			campaignAnimation: null,
			resultCards: [
				{ id: 1, titleKey: 'campaigns_card_1_title', descKey: 'campaigns_card_1_desc' },
				{ id: 2, titleKey: 'campaigns_card_2_title', descKey: 'campaigns_card_2_desc' },
				{ id: 6, titleKey: 'campaigns_card_3_title', descKey: 'campaigns_card_3_desc' },
				{ id: 5, titleKey: 'campaigns_card_4_title', descKey: 'campaigns_card_4_desc' },
				{ id: 3, titleKey: 'campaigns_card_5_title', descKey: 'campaigns_card_5_desc' },
				{ id: 4, titleKey: 'campaigns_card_6_title', descKey: 'campaigns_card_6_desc' },
			]
		};
	},
	mounted() {
		this.checkInterestStatus();
		this.$nextTick(() => this.loadCampaignsAnimation());
	},
	watch: {
		'uiStore.darkMode'() {
			this.destroyCampaignsAnimation();
			this.$nextTick(() => this.loadCampaignsAnimation());
		},
		'userStore.currBusinessId'(newVal, oldVal) {
			if (newVal !== null && newVal !== oldVal) {
				this.checkInterestStatus();
			}
		}
	},
	beforeUnmount() {
		this.destroyCampaignsAnimation();
	},
	methods: {
		checkInterestStatus() {
			const key = `campaigns_interest_${this.userStore.currBusinessId}`;
			this.isInterested = localStorage.getItem(key) === 'true';
		},
		destroyCampaignsAnimation() {
			if (this.campaignAnimation) {
				this.campaignAnimation.destroy();
				this.campaignAnimation = null;
			}
		},
		loadCampaignsAnimation() {
			if (typeof bodymovin === 'undefined') { return; }
			const path = this.uiStore.darkMode
				? 'https://img.qromo.io/img/section-console-campaigns/lottie-campagne-white-opt.json'
				: 'https://img.qromo.io/img/section-console-campaigns/lottie-campagne-dark-opt.json';
			this.campaignAnimation = bodymovin.loadAnimation({
				container: document.getElementById('campaigns-lottie-animation'),
				renderer: 'svg',
				loop: true,
				autoplay: true,
				path,
				rendererSettings: {
					preserveAspectRatio: 'xMidYMid meet'
				}
			});
		},
		handleInterestClick() {
			if (this.isInterested) return;

			this.isLoading = true;
			API.init().recordComingSoonInterest(
				this.userStore.currBusinessId,
				'campaigns',
				(resp) => {
					this.isLoading = false;
					if (resp && resp.result && resp.result === "OK") {
						this.isInterested = true;
						const key = `campaigns_interest_${this.userStore.currBusinessId}`;
						localStorage.setItem(key, 'true');
						this.showNotification('campaigns-interest-registered');
					} else {
						this.showNotification('generic-fail');
					}
				}
			);
		}
	}
};
</script>

<style scoped lang="scss">
.campaigns-container {
	max-width: 1200px;
	margin: 0 auto;
	padding: 32px 16px 64px;
	display: flex;
	flex-direction: column;
	gap: 80px;
}

/* ── SEZIONE 1: Hero ── */
.hero-section {
	display: flex;
	align-items: center;
	gap: 48px;
	background: var(--color-bg-primary-1);
	border-radius: 16px;

	@media (max-width: 768px) {
		flex-direction: column;
		gap: 32px;
	}
}


.hero-content {
	flex: 0 0 calc(50% - 24px);
	width: calc(50% - 24px);
	@media (max-width: 768px) {
		flex: none;
		width: 100%;
	}
}
.hero-title {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 10px;
	font-size: 28px;
	font-weight: 700;
	color: var(--color-txt);
	margin: 0 0 20px 0;
	line-height: 1.2;

	@media (max-width: 768px) {
		font-size: 22px;
	}
}

.meta-logo-inline {
	display: inline-flex;
	align-items: center;

	img {
		height: 25px;
		width: auto;
		@media (max-width: 1260px) {
			height: 22px;
		}
		@media (max-width: 768px) {
			height: 20px;
		}
	}
}

.hero-desc {
	font-size: 15px;
	line-height: 1.65;
	color: var(--color-txt-1);
	margin: 0 0 12px 0;
}

.social-logos {
	display: flex;
	align-items: center;
	gap: 12px;
	margin: 20px 0 28px;

	img {
		height: 32px;
		width: auto;
	}
}

.hero-cta {
	display: flex;
	align-items: center;
}

.hero-animation {
	flex: 0 0 calc(50% - 24px);
	width: calc(50% - 24px);
	background-color: var(--color-bg);
	border-radius: 18px;
	box-shadow: 5px 5px 40px -20px var(--color-box-shadow-4);
	@media (max-width: 768px) {
		flex: none;
		width: 100%;
	}
}

.lottie-container {
	width: 100%;
	min-height: 280px;
}

/* ── SEZIONE 2: Meta ── */
.meta-section {
	display: flex;
	align-items: center;
	gap: 48px;

	@media (max-width: 768px) {
		flex-direction: column;
		gap: 32px;
	}
}

.meta-visual {
	flex: 0 0 48%;

	picture {
		display: block;
		width: 100%;
	}

	img {
		display: block;
		width: 100%;
		border-radius: 16px;
		object-fit: cover;
	}

	@media (max-width: 768px) {
		flex: none;
		width: 100%;
	}
}

.meta-content {
	flex: 1;
}

.meta-title {
	font-size: 22px;
	font-weight: 700;
	color: var(--color-txt);
	margin: 0 0 20px 0;
	line-height: 1.3;
}

.meta-desc {
	font-size: 14px;
	line-height: 1.7;
	color: var(--color-txt-1);
	margin: 0 0 14px 0;
}

.meta-cta {
	margin-top: 28px;
	display: flex;
	align-items: center;
}

/* ── SEZIONE 3: Risultati ── */
.results-section {
	display: flex;
	flex-direction: column;
}

.results-title {
	font-size: 24px;
	font-weight: 700;
	color: var(--color-txt);
	margin: 0 0 32px 0;
}

.results-grid {
	display: flex;
	flex-wrap: wrap;
	gap: 24px;
}

.result-card {
	flex: 1 1 calc(33.333% - 16px);
	min-width: 240px;
	padding: 24px;
	border-radius: 13px;
	background-color: var(--color-bg);
	box-shadow: 0 3px 5px var(--color-box-shadow-3);

	@media (max-width: 768px) {
		flex: 1 1 100%;
	}

	@media (min-width: 500px) and (max-width: 768px) {
		flex: 1 1 calc(50% - 12px);
	}
}

.card-icon {
	width: 30px;
	height: 30px;
	margin-bottom: 16px;
}

.card-title {
	font-size: 15px;
	font-weight: 600;
	color: var(--color-txt);
	margin: 0 0 8px 0;
}

.card-desc {
	font-size: 13px;
	line-height: 1.6;
	color: var(--color-txt-1);
	margin: 0;
}

.results-cta {
	display: flex;
	justify-content: center;
	margin-top: 48px;
}

/* ── Shared: already registered ── */
.already-registered {
	display: flex;
	align-items: center;
	gap: 8px;
	color: var(--color-primary);
	font-weight: 600;
	font-size: 15px;

	svg {
		flex-shrink: 0;
	}
}

.cta-btn {
	height: 44px;
}
</style>
